import { useEffect, useMemo, useState } from "react";
import { ajustarInventario } from "../../../services/admin/inventario";

function InventoryAdjustModal({ isOpen, onClose, product, onSuccess }) {
  const [cantidadStr, setCantidadStr] = useState("0");
  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null); 

  const getNiceErrorMessage = (error, fallback = "Error adjusting inventory.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 240 ? clean.slice(0, 240) + "…" : clean;
  };

  const title = useMemo(() => {
    if (!product) return "Adjust Inventory";
    const part = product.no_parte ? ` • ${product.no_parte}` : "";
    return `${product.nombre}${part}`;
  }, [product]);

  useEffect(() => {
    if (!isOpen) return;

    setCantidadStr("0");
    setObservacion("");
    setLoading(false);
    setAlert(null);
  }, [isOpen, product?.producto_id]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const cantidad = useMemo(() => {
    const n = Number(cantidadStr);
    return Number.isFinite(n) && Number.isInteger(n) ? n : null;
  }, [cantidadStr]);

  if (!isOpen || !product) return null;

  const validate = () => {
    if (cantidad === null) {
      return "Enter a valid integer (e.g. 5 or -3).";
    }
    if (cantidad === 0) {
      return "Adjustment quantity cannot be zero.";
    }
    if (!observacion.trim()) {
      return "Reason for adjustment is required.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const validationError = validate();
    if (validationError) {
      setAlert({ type: "error", message: validationError });
      return;
    }

    try {
      setLoading(true);

      await ajustarInventario({
        producto_id: product.producto_id,
        cantidad,
        observacion: observacion.trim(),
      });

      setAlert({ type: "success", message: "Inventory adjusted successfully." });
      onSuccess?.();

      onClose?.();
    } catch (err) {
      console.error("Adjust inventory failed:", err);
      setAlert({ type: "error", message: getNiceErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const closeIfBackdrop = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  return (
    <div className="adc-modal-backdrop" onMouseDown={closeIfBackdrop}>
      <div className="adc-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3 className="adc-modal-title">Adjust Inventory</h3>

        <p className="adc-modal-subtitle">{title}</p>

        {alert && (
          <div
            className={`adc-alert ${alert.type === "error" ? "adc-alert--error" : "adc-alert--success"}`}
          >
            <span>{alert.message}</span>
            <button
              type="button"
              className="adc-alert-x"
              onClick={() => setAlert(null)}
              disabled={loading}
              aria-label="Close alert"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="adc-modal-form">
          <label>
            Adjustment
            <input
              type="number"
              step="1"
              value={cantidadStr}
              onChange={(e) => setCantidadStr(e.target.value)}
              placeholder="-2 or 5"
              required
              disabled={loading}
            />
          </label>

          <label>
            Reason *
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Explain why this adjustment is needed"
              required
              disabled={loading}
            />
          </label>

          <div className="adc-modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button type="submit" className="btn-warning" disabled={loading}>
              {loading ? "Saving..." : "Apply Adjustment"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .adc-alert{
          margin: 12px 0 0;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 12px;
          font-weight: 700;
          font-size: 13px;
        }
        .adc-alert--error{
          background:#FEF2F2;
          border-color:#FECACA;
          color:#7F1D1D;
        }
        .adc-alert--success{
          background:#ECFDF5;
          border-color:#A7F3D0;
          color:#065F46;
        }
        .adc-alert-x{
          border:none;
          background:transparent;
          cursor:pointer;
          font-size: 16px;
          opacity:.7;
        }
        .adc-alert-x:hover{ opacity: 1; }
      `}</style>
    </div>
  );
}

export default InventoryAdjustModal;
